import React, { useMemo, useState } from 'react';
import AdvancePayment from './Advancepayment';
import EngineerPayment from './Engineerpayment';
import Welfare from './Welfare';

// ---------------------------------------------------------------------------
// PAYMENTS MODULE — combines the 3 separate modules into one screen with tabs.
// Also exports the shared auth-check helper + "not authenticated" UI so
// AdvancePayment / EngineerPayment / Welfare can all reuse the same gate.
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'advance', label: 'Advance Payment' },
  { key: 'payroll', label: 'Engineer Payment', hideFromRoles: ['ENGINEER'] },
  { key: 'welfare', label: 'Welfare' },
];

const ADMIN_VARIANTS = ['ADMIN', 'SUPERENGINEER', 'SUPER_ENGINEER', 'SUPER ENGINEER'];

function normalizeRole(rawRoles) {
  if (!rawRoles) return null;
  const list = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
  const roleNames = list
    .map((r) => (typeof r === 'string' ? r : r?.roleName))
    .filter(Boolean)
    .map((r) => r.toUpperCase());

  const isEngineer = roleNames.includes('ENGINEER');
  const isAdmin = roleNames.some((r) => ADMIN_VARIANTS.includes(r));

  if (isAdmin) {
    const isSuperEngineer = roleNames.some((r) =>
      ['SUPERENGINEER', 'SUPER_ENGINEER', 'SUPER ENGINEER'].includes(r)
    );
    return isSuperEngineer && !roleNames.includes('ADMIN') ? 'SUPER_ENGINEER' : 'ADMIN';
  }
  if (isEngineer) return 'ENGINEER';
  return roleNames[0] || null;
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('NagpurProperties');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      id: parsed.id,
      name: parsed.name,
      email: parsed.email,
      role: normalizeRole(parsed.role),
      token: parsed.token,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Shared exports — used by Payments itself AND by AdvancePayment,
// EngineerPayment, Welfare (import { isAuthenticated, AuthRequired } from './Payments').
// ---------------------------------------------------------------------------
export function isAuthenticated(currentUser) {
  return Boolean(currentUser && currentUser.id && currentUser.role && currentUser.token);
}

export function AuthRequired() {
  return (
    <div style={styles.authWrapper}>
      <div style={styles.authIcon}>🔒</div>
      <h3 style={styles.authTitle}>Authentication Required</h3>
      <p style={styles.authText}>
        Your session has expired or you're not logged in. Please log in again to continue.
      </p>
      <button style={styles.authBtn} onClick={() => (window.location.href = '/login')}>
        Go to Login
      </button>
    </div>
  );
}

export { getStoredUser };

// ---------------------------------------------------------------------------
// Root export
// ---------------------------------------------------------------------------
export default function Payments({ currentUser: currentUserProp }) {
  const currentUser = useMemo(() => currentUserProp || getStoredUser(), [currentUserProp]);

  // Engineers never see the "Engineer Payment" (payroll) tab — that's the
  // admin/super-engineer view of engineer payroll, not something engineers
  // get to browse themselves.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.hideFromRoles?.includes(currentUser?.role)),
    [currentUser?.role]
  );

  const [activeTab, setActiveTab] = useState('advance');

  // If the active tab is ever one this role can't see (e.g. role changes
  // after login, or a stale tab from a previous session), fall back to the
  // first tab this role is actually allowed to view.
  const effectiveTab = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : visibleTabs[0]?.key;

  if (!isAuthenticated(currentUser)) {
    return <AuthRequired />;
  }

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.title}>Payments</h2>

      <nav style={styles.tabRow}>
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={effectiveTab === t.key ? styles.tabActive : styles.tab}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div>
        {effectiveTab === 'advance' && <AdvancePayment currentUser={currentUser} />}
        {effectiveTab === 'payroll' && <EngineerPayment currentUser={currentUser} />}
        {effectiveTab === 'welfare' && <Welfare currentUser={currentUser} />}
      </div>
    </div>
  );
}

const styles = {
  wrapper: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#222' },
  title: { margin: '0 0 16px' },
  tabRow: { display: 'flex', gap: 8, borderBottom: '1px solid #eee', marginBottom: 20 },
  tab: { padding: '10px 18px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#777', cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { padding: '10px 18px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#2d5bd7', cursor: 'pointer', borderBottom: '2px solid #2d5bd7' },
  authWrapper: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', padding: '60px 24px', background: '#fff', border: '1px solid #eee',
    borderRadius: 10, maxWidth: 420, margin: '40px auto',
  },
  authIcon: { fontSize: 32, marginBottom: 12 },
  authTitle: { margin: '0 0 8px', fontSize: 18, color: '#222' },
  authText: { margin: '0 0 20px', fontSize: 14, color: '#777', lineHeight: 1.5 },
  authBtn: {
    padding: '10px 24px', border: 'none', borderRadius: 6, background: '#2d5bd7',
    color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14,
  },
};